import { Paper, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { AssignmentStatus, Piece, PieceType, TrackingNumberStatus, Workflow } from '@apiContract';
import Footer from '@components/DrawerContent/Footer';
import Title from '@components/DrawerContent/Title';
import { ReceiveToggleButtonGroup } from 'src/components/Capture/components/ReceiveConfig/ReceiveToggleButtonGroup';
import { workflowMailOptions, workflowPackagesOptions } from 'src/constants/workflows';
import { useSettings } from 'src/hooks/useSettings';
import { getPieceTypeByWorkflow, isPieceInLocker } from 'src/utils/piece';

import ContainerName from './components/ContainerName';
import LockerContent from './components/Lockers';
import NotLockersContent from './components/NotLockers';
import { sxMain, sxP20, sxPaper, sxSection } from './styles';
import { useLocationId } from './useLocationId';

type LocationIDProps = {
  piece: Piece;
  onChange: (data: Piece) => void;
  onClose: () => void;
  context?: 'capture';
};

const notAssignedErrors: { [key in Workflow]?: string } = {
  [Workflow.HighDensityMail]: 'Item cannot be moved to High Density Mail workflow while unassigned',
  [Workflow.MailBox]: 'Item cannot be moved to Mailbox workflow while unassigned',
};

const LocationID = ({ piece, onChange, onClose, context }: LocationIDProps) => {
  const [isInternalLoading, setIsLoading] = useState(false);

  const { pieceLocation, workflow, sentStatus, pieceSummary } = piece;

  const receiveSettings = useSettings(['LockerReservationEnabled', 'LockersWorkflowEnable']) as {
    LockersWorkflowEnable: boolean;
    LockerReservationEnabled: boolean;
  };
  const isInLocker = isPieceInLocker(piece);

  const errors =
    pieceSummary?.pieceAssignmentStatus !== AssignmentStatus.Assigned ? { ...notAssignedErrors } : undefined;
  if (errors) {
    delete errors[workflow];
  }

  const {
    setSpot,
    spot,
    error,
    onSaveLocation,
    saveDisabled,
    isLoading: updatePieceLoading,
    stateWorkflow,
    onChangeWorkflow,
  } = useLocationId(piece, onChange, onClose, context);

  return (
    <Paper elevation={3} sx={sxPaper}>
      <Title title="Storage Location" onClose={onClose} />
      <Stack spacing={3} sx={sxMain}>
        <ContainerName containerTitle={pieceLocation?.locationCodeFormatted} piece={piece} />
        <Stack sx={sxSection} spacing={3}>
          <Typography variant="subtitle1">Workflow</Typography>
          <ReceiveToggleButtonGroup
            direction="column"
            config={
              getPieceTypeByWorkflow(stateWorkflow) === PieceType.Mail
                ? workflowMailOptions(errors)
                : workflowPackagesOptions(
                    receiveSettings.LockersWorkflowEnable,
                    workflow,
                    piece.pieceSummary?.pieceAssignmentStatus === AssignmentStatus.Assigned,
                    piece.pieceSummary?.trackingNumberStatus === TrackingNumberStatus.Valid,
                  )
            }
            disabled={sentStatus || isInLocker}
            groupTooltip={sentStatus ? 'Workflow cannot be changed after item has been sent' : undefined}
            value={stateWorkflow}
            onChange={onChangeWorkflow}
          />
        </Stack>

        {[Workflow.Carrier, Workflow.DateReceived, Workflow.Location].includes(stateWorkflow) && (
          <NotLockersContent
            piece={piece}
            workflow={stateWorkflow}
            setIsLoading={setIsLoading}
            isLoadingContainer={isInternalLoading}
            onChange={setSpot}
            spot={spot}
          />
        )}
        {stateWorkflow === Workflow.Lockers && receiveSettings.LockerReservationEnabled && (
          <LockerContent piece={piece} error={error} spot={spot} onChange={setSpot} />
        )}
      </Stack>
      <Footer
        sx={sxP20}
        isLoading={updatePieceLoading || isInternalLoading}
        handleSubmit={onSaveLocation}
        title="Save Changes"
        disabled={saveDisabled}
      />
    </Paper>
  );
};

export default LocationID;
